import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Logger } from "@/lib/utils/logger";

const logger = new Logger('ForgotPassword');

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }
    
    setLoading(true);
    
    try {
      logger.info('Initiating password reset request', { email });
      
      // Call the custom Supabase function endpoint for password reset with authorization
      const response = await fetch('https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1/reset_password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh1cWtnZXVzYXNvc3h0Y2ZoYndqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzYzMDg1OTUsImV4cCI6MjA1MTg4NDU5NX0.HF4gYNKUEct3xetUJ3NwB1pxgBGBSE2V6CY20iF1Ivs'
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        logger.error('Failed to send reset email', { error: data.error || response.statusText });
        setError(data.error || 'Failed to send reset email. Please try again.');
        return;
      }
      
      logger.info('Reset email sent successfully', { response: data });
      setEmailSent(true);
      
      toast({
        title: "Reset email sent",
        description: "If an account exists with this email, you'll receive password reset instructions.",
        variant: "default"
      });
    } catch (err) {
      logger.error('Unexpected error sending reset email', { error: err });
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <Link to="/sign-in" className="inline-flex items-center text-gray-400 hover:text-white mb-12">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Sign In
      </Link>

      <div className="max-w-md mx-auto space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Reset Password</h1>
          <p className="text-gray-400">
            We'll send you instructions to reset your password
          </p>
        </div>
        
        {error && (
          <Alert variant="destructive" className="bg-red-900/50 border-red-600">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {emailSent ? (
          <div className="bg-green-900/20 border border-green-800 rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-center">
              <Mail className="w-12 h-12 text-green-400" />
            </div>
            
            <div className="text-center space-y-3">
              <h2 className="text-xl font-medium">Check your inbox</h2>
              <p className="text-gray-300">
                We've sent password reset instructions to <span className="font-semibold">{email}</span>
              </p>
              <p className="text-gray-400 text-sm">
                If you don't see the email, check your spam folder or make sure you entered the correct email address.
              </p>
            </div>
            
            <div className="pt-2">
              <Button
                className="w-full bg-white text-black hover:bg-gray-200"
                onClick={() => setEmailSent(false)}
              >
                Try a different email
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 space-y-6">
            <div className="flex items-center justify-center">
              <Mail className="w-12 h-12 text-purple-400" />
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="block">
                  Your Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="bg-gray-900 border-gray-800 text-white"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Instructions"}
              </Button>
            </form>
          </div>
        )}

        <div className="text-center">
          <Link to="/sign-in" className="text-gray-400 hover:text-white text-sm">
            Remember your password? Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};
